import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { NodeType } from '@ui/__generated__/graphql'
import {
  AttributeKey,
  DatableLog,
  DatableTrace,
  QueryParams,
  SimulateEventParams,
  TelemetryEvent,
} from '@ui/interfaces'
import { getStartAndEnd } from '@ui/lib/helpers/timeWindow'
import { SimulateEventPipelineLazyQuery } from '@ui/loaders/processor'
import { TelemetryLazyQuery } from '@ui/loaders/telemetry'
import { useNodeAndEdgeStore, useTelemetryStore, useTimeStore } from '@ui/stores'

// Create the context
const PipelineDataContext = createContext<{
  events: {
    input: DatableLog | DatableTrace
    output: DatableLog | DatableTrace
  }[]
  uniqueKeys: AttributeKey[]
  selectedEvent: DatableLog | DatableTrace | null
  selectedOutput: DatableLog | DatableTrace | null
  loading: boolean
  errorLog: string | null
  consoleLog: string | null
  fetchPipelineData: (sampleEvent?: DatableLog | DatableTrace) => Promise<void>
  refreshPipelineData: () => Promise<void>
} | null>(null)

export const usePipelineDataContext = () => {
  const context = useContext(PipelineDataContext)
  if (!context) {
    throw new Error('usePipelineDataContext must be used within a PipelineDataProvider')
  }
  return context
}

// Provider Component
export const PipelineDataProvider = ({ children }: { children: React.ReactNode }) => {
  const [events, setEvents] = useState<
    {
      input: DatableLog | DatableTrace
      output: DatableLog | DatableTrace
    }[]
  >([])
  const [selectedEvent, setSelectedEvent] = useState<DatableLog | DatableTrace | null>(null)
  const [selectedOutput, setSelectedOutput] = useState<DatableLog | DatableTrace | null>(null)
  const [loading, setLoading] = useState(true)
  const [fetchedUniqueKeys, setFetchedUniqueKeys] = useState<AttributeKey[]>([])
  const [telemetryLazyQuery] = TelemetryLazyQuery()
  const [simulateEventPipelineLazyQuery] = SimulateEventPipelineLazyQuery()
  const [errorLog, setErrorLog] = useState<string | null>(null)
  const [consoleLog, setConsoleLog] = useState<string | null>(null)

  const { nodes, edges, currentNodeId } = useNodeAndEdgeStore(
    useShallow((state) => ({
      nodes: state.nodes,
      edges: state.edges,
      currentNodeId: state.currentNodeId,
    }))
  )

  const firehoseNode = useMemo(() => nodes.find((node) => node.type === NodeType.Firehose), [nodes])
  const currentNode = useMemo(() => nodes.find((node) => node.id === currentNodeId), [nodes, currentNodeId])

  const timewindow = useTimeStore(useShallow((state) => state.timewindow))
  const { dataSource, whereClause, logs, spans, pinnedSampleEvents, uniqueKeysLogs, uniqueKeysSpans } =
    useTelemetryStore(
      useShallow((state) => ({
        dataSource: state.dataSource,
        whereClause: state.whereClause,
        logs: state.logs,
        spans: state.spans,
        pinnedSampleEvents: state.pinnedSampleEvents,
        uniqueKeysLogs: state.uniqueKeysLogs,
        uniqueKeysSpans: state.uniqueKeysSpans,
      }))
    )

  const startAndEnd = useMemo(() => getStartAndEnd(timewindow), [timewindow])

  const queryParams = useMemo(() => {
    if (!firehoseNode) return null
    return {
      from: dataSource,
      where: whereClause,
      limit: 100,
      nodeId: firehoseNode.id,
      startTime: startAndEnd.startTime,
      endTime: startAndEnd.endTime,
    }
  }, [dataSource, whereClause, firehoseNode, startAndEnd])

  const simulateEventParams = useMemo(() => {
    if (!currentNode) return null
    return {
      selectedNodeId: currentNode.id,
      selectedNodeCode: currentNode.data.code || '',
      selectedEvent: selectedEvent || (dataSource === 'logs' ? logs[0]?.record : spans[0]?.record),
      selectedEventType: dataSource === 'logs' ? ('log' as const) : ('span' as const),
      events: dataSource === 'logs' ? logs : spans,
      nodes,
      edges,
      queryParams,
    }
  }, [currentNode?.data.code, currentNode?.id, dataSource, edges, logs, nodes, queryParams, selectedEvent, spans])

  const fetchPipelineData = useCallback(
    async (sampleEvent?: DatableLog | DatableTrace) => {
      try {
        if (!queryParams || !simulateEventParams || loading) return
        setLoading(true)

        let telemetryEvents: TelemetryEvent<DatableLog | DatableTrace>[] = []

        if (!pinnedSampleEvents) {
          // Fetch telemetry data
          const telemetryResponse = await telemetryLazyQuery({ variables: { queryParams: queryParams as QueryParams } })
          if (telemetryResponse?.data?.telemetry?.events) {
            telemetryEvents = telemetryResponse?.data?.telemetry?.events as unknown as TelemetryEvent<
              DatableLog | DatableTrace
            >[]
            setFetchedUniqueKeys((telemetryResponse?.data?.telemetry?.uniqueKeys as AttributeKey[]) || [])
          }
        }

        if (pinnedSampleEvents) {
          telemetryEvents = dataSource === 'logs' ? logs : spans
        }

        // Fetch simulation data
        const simulationData = await simulateEventPipelineLazyQuery({
          variables: {
            simulateEventParams: {
              ...simulateEventParams,
              selectedEvent: telemetryEvents?.[0]?.record || ({} as DatableLog | DatableTrace),
              events: sampleEvent
                ? [
                    {
                      record: sampleEvent,
                      metadata: {
                        datatype: dataSource === 'logs' ? 'log' : 'span',
                      },
                    },
                    ...telemetryEvents,
                  ]
                : telemetryEvents,
            } as SimulateEventParams,
          },
        })

        const {
          events: simulationEvents = [],
          selectedEvent,
          // @ts-expect-error simulateEvent is not typed correctly
        } = simulationData?.data?.pipeline.simulateEvent || {}
        const inputEvents = simulationEvents.map((e) => e.input)
        const outputEvents = simulationEvents.map((e) => e.output)

        setEvents(
          inputEvents.map((event, index) => ({
            input: event as DatableLog | DatableTrace,
            output: outputEvents[index] as DatableLog | DatableTrace,
          }))
        )
        setSelectedEvent(inputEvents?.[0] || null)
        setSelectedOutput(outputEvents?.[0] || null)
        setErrorLog(selectedEvent?.errorLog[0] || null)
        setConsoleLog(selectedEvent?.consoleLog[0] || null)
      } catch (error) {
        console.error('Error fetching pipeline data:', error)
      } finally {
        setLoading(false)
      }
    },
    [queryParams, simulateEventParams, telemetryLazyQuery, simulateEventPipelineLazyQuery]
  )

  const refreshPipelineData = useCallback(async () => {
    try {
      setLoading(true)
      const telemetryData = await telemetryLazyQuery({
        variables: {
          queryParams: {
            from: dataSource,
            where: whereClause,
            limit: 100,
            nodeId: firehoseNode?.id,
            startTime: getStartAndEnd(timewindow).startTime,
            endTime: getStartAndEnd(timewindow).endTime,
          },
        },
      })

      const { events: telemetryEvents } = telemetryData?.data?.telemetry || {}

      const simulationData = await simulateEventPipelineLazyQuery({
        variables: {
          simulateEventParams: {
            ...simulateEventParams,
            events: telemetryEvents as unknown as TelemetryEvent<DatableLog | DatableTrace>[],
            selectedEvent: telemetryEvents?.[0]?.record,
          } as SimulateEventParams,
        },
      })

      const { events: simulationEvents = [] } = simulationData?.data?.pipeline.simulateEvent || {}
      const inputEvents = simulationEvents.map((e) => e.input)
      const outputEvents = simulationEvents.map((e) => e.output)

      setEvents(
        inputEvents.map((event, index) => ({
          input: event as DatableLog | DatableTrace,
          output: outputEvents[index] as DatableLog | DatableTrace,
        }))
      )
    } catch (error) {
      console.error('Error refreshing pipeline data:', error)
    } finally {
      setLoading(false)
    }
  }, [fetchPipelineData])

  useEffect(() => {
    fetchPipelineData()
  }, [currentNodeId, currentNode?.data.code, currentNode?.data.steps, dataSource])

  const uniqueKeys = useMemo(() => {
    return pinnedSampleEvents ? (dataSource === 'logs' ? uniqueKeysLogs : uniqueKeysSpans) : fetchedUniqueKeys
  }, [dataSource, fetchedUniqueKeys, pinnedSampleEvents, uniqueKeysLogs, uniqueKeysSpans])

  const value = {
    events,
    selectedEvent,
    selectedOutput,
    loading,
    uniqueKeys,
    errorLog,
    consoleLog,
    fetchPipelineData,
    refreshPipelineData,
  }

  return <PipelineDataContext.Provider value={value}>{children}</PipelineDataContext.Provider>
}
